import React, { useState, useEffect, useCallback } from "react";
import Header from "@/components/layout/Header";
import Sidebar from "@/components/layout/Sidebar";
import BottomNav from "@/components/layout/BottomNav";
import { usePedidoStatus } from "@/components/realtime/usePedidoStatus";
import AuthContext, { useAuth } from "@/hooks/useAuth";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import authService from "@/services/authService";
import companyService from "@/services/companyService";
import productService from "@/services/productService";
import { getApiErrorMessage } from "@/services/apiClient";
import { isOwner, isGlobalOwner } from "@/lib/permissions";
import { setCompanyContext } from "@/lib/company";
import menuService, { MENUS_CHANGED_EVENT } from "@/services/menuService";
import setupAxiosInterceptors from "@/services/axiosInterceptor";
import ErrorModal from "@/components/error/ErrorModal";
import useErrorStore from "@/store/errorStore";
import "./App.css";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Label } from "./components/ui/label";

import IngredientsManagement from "@/components/ingredients/IngredientsManagement";
import UploadsManagementModal from "@/components/uploads/UploadsManagementModal";
import OrdersManagement from "@/components/orders/OrdersManagement";
import ProductsManagement from "@/components/products/ProductsManagement";
import CategoriesManagement from "@/components/categories/CategoriesManagement";
import EtiqueLabelGenerator from "@/components/etique/EtiqueLabelGenerator";
import Dashboard from "@/components/Dashboard";
import UsersManagement from "@/components/users/UsersManagement";
import AddressesManagement from "@/components/addresses/AddressesManagement";
import PaymentMethodsManagement from "@/components/payments/PaymentMethodsManagement";
import PaymentIntegrationsManagement from "@/components/payment-integrations/PaymentIntegrationsManagement";
import StorageLocationsManagement from "@/components/storage-locations/StorageLocationsManagement";
import PixManagement from "@/components/pix/PixManagement";
import CompaniesManagement from "@/components/companies/CompaniesManagement";
import FreightManagement from "@/components/freight/FreightManagement";
import EquipmentsManagement from "@/components/equipments/EquipmentsManagement";
import StocksManagement from "@/components/stock/StocksManagement";
import CouponsManagement from "@/components/coupons/CouponsManagement";
import ClientsManagement from "@/components/clients/ClientsManagement";
import PrintOrderReceipt from "@/components/orders/PrintOrderReceipt";

// Interceptors do apiClient registrados uma vez, antes de qualquer requisição
setupAxiosInterceptors();

// Erro lançado quando o usuário do painel não tem empresa vinculada
const NO_COMPANY_ERROR = "NO_COMPANY";
const NO_COMPANY_MESSAGE =
  "Usuário sem empresa vinculada. Fale com o administrador do sistema.";

/**
 * Sessão do painel.
 * - A empresa do usuário vem da claim company_code do JWT; o backend filtra
 *   todos os dados por ela.
 * - Usuário sem empresa (e que não é owner) não acessa o painel: a API
 *   devolveria 403 em tudo.
 * - Owner global (sem a claim) escolhe a empresa no Header; a escolha vai no
 *   header X-Company-Code (ver axiosInterceptor).
 */
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState("");
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState(
    authService.getSelectedCompanyCode(),
  );

  /**
   * Bloqueia usuário sem empresa vinculada (exceto owner global)
   */
  const ensureCompany = (userData) => {
    if (!userData.company_code && !isOwner(userData)) {
      authService.logout();
      const error = new Error(NO_COMPANY_MESSAGE);
      error.code = NO_COMPANY_ERROR;
      throw error;
    }
    return userData;
  };

  useEffect(() => {
    if (authService.isAuthenticated() && authService.isTokenValid()) {
      fetchUserInfo();
    } else {
      authService.logout();
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = ensureCompany(await authService.getCurrentUser());
      setSelectedCompanyCode(authService.getSelectedCompanyCode());
      setUser(response);
    } catch (error) {
      authService.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Nome da empresa atual e, para o owner global, a lista do seletor
   */
  useEffect(() => {
    if (!user) {
      setCompanyName("");
      setCompany(null);
      setCompanies([]);
      return;
    }

    let cancelled = false;
    const loadCompanies = async () => {
      try {
        if (isGlobalOwner(user)) {
          const list = await companyService.getAllCompanies({
            silent: true,
            skipCompanyScope: true,
          });
          if (!cancelled) setCompanies(list);
        } else if (user.company_code) {
          const current = await companyService.getCompanyByCode(
            user.company_code,
            { silent: true },
          );
          if (!cancelled) {
            setCompany(current || null);
            setCompanyName(current?.name || "");
          }
        }
      } catch (error) {
        if (!cancelled) {
          setCompany(null);
          setCompanyName("");
        }
      }
    };
    loadCompanies();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!isGlobalOwner(user)) return;
    const selected = companies.find((c) => c.code === selectedCompanyCode);
    setCompany(selected || null);
    setCompanyName(selected?.name || "");
  }, [user, companies, selectedCompanyCode]);

  // Nome da empresa para comanda, impressão e WhatsApp (fora do React)
  useEffect(() => {
    setCompanyContext({ name: companyName, companies });
  }, [companyName, companies]);

  const login = async (email, password) => {
    await authService.login(email, password);
    const userData = ensureCompany(await authService.getCurrentUser());
    setSelectedCompanyCode(authService.getSelectedCompanyCode());
    setUser(userData);
    return true;
  };

  const logout = () => {
    authService.logout();
    setSelectedCompanyCode(null);
    setUser(null);
  };

  /**
   * Owner global: troca a empresa em foco (null = todas as empresas)
   */
  const selectCompany = useCallback((code) => {
    authService.setSelectedCompanyCode(code || null);
    localStorage.removeItem("printOrder");
    setSelectedCompanyCode(code || null);
  }, []);

  const globalOwner = isGlobalOwner(user);
  // Empresa efetiva das requisições (null = owner vendo todas)
  const companyCode = user?.company_code || (globalOwner ? selectedCompanyCode : null);

  /**
   * Abre/pausa a loja da empresa atual (PUT /companies/{code}/availability)
   * e atualiza o status exibido no Header e no Dashboard.
   */
  const setStoreAvailability = useCallback(
    async (available) => {
      if (!companyCode) return null;
      const updated = await companyService.setAvailability(
        companyCode,
        available,
      );
      const next = { ...(company || {}), ...(updated || {}), available };
      setCompany(next);
      if (globalOwner) {
        setCompanies((list) =>
          list.map((c) => (c.code === companyCode ? { ...c, available } : c)),
        );
      }
      return next;
    },
    [companyCode, company, globalOwner],
  );

  /**
   * Mantém Header/Dashboard em dia quando outra tela altera a empresa
   * (ex.: abrir/fechar a loja em Empresas).
   */
  const syncCompany = useCallback((updated) => {
    if (!updated?.code) return;
    setCompany((current) =>
      current?.code === updated.code ? { ...current, ...updated } : current,
    );
    setCompanies((list) =>
      list.map((c) => (c.code === updated.code ? { ...c, ...updated } : c)),
    );
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        companyCode,
        companyName,
        company,
        setStoreAvailability,
        syncCompany,
        companies,
        isGlobalOwner: globalOwner,
        selectedCompanyCode: globalOwner ? selectedCompanyCode : null,
        selectCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login(username, password);
    } catch (error) {
      let errorMessage = "Credenciais inválidas";

      if (error.code === NO_COMPANY_ERROR) {
        errorMessage = NO_COMPANY_MESSAGE;
      } else if (error.response) {
        if (error.response.status === 401) {
          errorMessage = "Usuário ou senha incorretos";
        } else if (error.response.status === 403) {
          errorMessage = NO_COMPANY_MESSAGE;
        } else if (error.response.status >= 500) {
          errorMessage = "Erro no servidor. Tente novamente mais tarde.";
        } else {
          errorMessage =
            getApiErrorMessage(error) || "Erro ao fazer login";
        }
      } else if (error.request) {
        errorMessage = "Sem resposta do servidor. Verifique sua conexão.";
      } else {
        errorMessage = "Erro ao processar login: " + error.message;
      }

      setError(errorMessage);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md border bg-card shadow-ambient">
        <CardHeader className="items-center text-center pb-6">
          <img
            src="/assets/logo_cardapio.jpg"
            alt="Cardápio Bonfim"
            className="mb-4 h-24 w-24 rounded-full bg-white object-contain ring-4 ring-secondary"
          />
          <CardTitle className="font-display text-3xl font-bold tracking-tight text-primary">
            Cardápio Bonfim
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            Gestor do cardápio e delivery
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-semibold">
                E-mail ou Usuário
              </Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-12 bg-white focus-visible:ring-[3px] focus-visible:ring-brand-pistachio/40 focus-visible:border-primary"
                placeholder="Digite seu e-mail ou usuário"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold">
                Senha
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 bg-white focus-visible:ring-[3px] focus-visible:ring-brand-pistachio/40 focus-visible:border-primary"
                placeholder="Digite sua senha"
              />
            </div>
            {error && (
              <div
                role="alert"
                className="rounded-xl border border-destructive/20 bg-destructive/10 px-3 py-2 text-center text-sm text-destructive"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="h-12 w-full rounded-full bg-primary text-base font-bold text-primary-foreground shadow-ambient hover:bg-brand-dark"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Mensagem exibida quando o usuário não tem acesso a nenhuma tela ou o menu falhou
const MenuStatusMessage = ({ title, message, onRetry }) => (
  <div className="flex items-center justify-center h-96">
    <Card className="max-w-md w-full text-center shadow-soft">
      <CardHeader>
        <CardTitle className="font-display text-xl">{title}</CardTitle>
        <CardDescription>{message}</CardDescription>
      </CardHeader>
      {onRetry && (
        <CardContent>
          <Button variant="outline" onClick={onRetry}>
            Tentar novamente
          </Button>
        </CardContent>
      )}
    </Card>
  </div>
);

// Dashboard Component
const AdminPanel = () => {
  // Owner global: trocar de empresa remonta a tela atual e descarta os dados
  const { selectedCompanyCode, companyCode } = useAuth();
  const companyScopeKey = selectedCompanyCode || "all";
  const [activeTab, setActiveTab] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [orderFilterStatus, setOrderFilterStatus] = useState("");
  // Pedido a abrir na Gestão de Pedidos (vindo de outra tela)
  const [orderFocusCode, setOrderFocusCode] = useState(null);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menusLoading, setMenusLoading] = useState(true);
  const [menusError, setMenusError] = useState(false);

  const allowedLinks = menuItems.map((item) => item.link);
  const canAccess = (link) => allowedLinks.includes(link);

  // Pedidos "Aguardando" (tempo real/polling) só para quem tem o menu "orders";
  // alimenta o sino do Header, a barra inferior e a atualização do Dashboard
  const { pedido: pendingCount } = usePedidoStatus(
    canAccess("orders"),
    companyCode,
  );

  useEffect(() => {
    fetchMenus();

    // Recarrega a sidebar quando menus/permissões forem alterados no painel
    const handleMenusChanged = () => fetchMenus({ silent: true });
    window.addEventListener(MENUS_CHANGED_EVENT, handleMenusChanged);
    return () =>
      window.removeEventListener(MENUS_CHANGED_EVENT, handleMenusChanged);
  }, []);

  const canAccessEtiquetas = canAccess("etiquetas");

  useEffect(() => {
    // Produtos são usados apenas pela tela de etiquetas
    if (canAccessEtiquetas) {
      fetchProducts();
    }
  }, [canAccessEtiquetas, companyScopeKey]);

  /**
   * Carrega o menu do usuário logado (GET /menus) e garante que a aba ativa
   * seja uma das permitidas: "dashboard" se liberado, senão o primeiro menu.
   * `silent`: recarrega sem spinner (não desmonta a tela aberta) e, se falhar,
   * mantém o menu atual.
   */
  const fetchMenus = async ({ silent = false } = {}) => {
    if (!silent) {
      setMenusLoading(true);
      setMenusError(false);
    }
    try {
      const menus = await menuService.getMyMenus();
      setMenuItems(menus);
      const links = menus.map((item) => item.link);
      setActiveTab((current) => {
        if (current && links.includes(current)) return current;
        if (links.includes("dashboard")) return "dashboard";
        return links[0] || null;
      });
    } catch (error) {
      if (!silent) {
        setMenuItems([]);
        setMenusError(true);
      }
    } finally {
      if (!silent) {
        setMenusLoading(false);
      }
    }
  };

  useEffect(() => {
    // Limpa o filtro de status quando mudar de tab (exceto quando vai para orders)
    if (activeTab !== "orders") {
      setOrderFilterStatus("");
      setOrderFocusCode(null);
    }
  }, [activeTab]);

  // Função para trocar de página com loading
  const handleTabChange = (tabId) => {
    // Só navega para telas liberadas pelo menu do usuário
    if (!canAccess(tabId)) {
      setSidebarOpen(false);
      return;
    }

    setIsLoadingPage(true);
    setActiveTab(tabId);
    setSidebarOpen(false);
    window.scrollTo({ top: 0 });

    // Simula tempo de carregamento para transição suave
    setTimeout(() => {
      setIsLoadingPage(false);
    }, 300);
  };

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const clearOrderFocus = useCallback(() => setOrderFocusCode(null), []);

  // Abre o pedido na Gestão de Pedidos (ex.: clique na reserva do Estoque)
  const openOrder = (code) => {
    if (!code || !canAccess("orders")) return;
    setOrderFocusCode(String(code));
    handleTabChange("orders");
  };

  const fetchProducts = async () => {
    try {
      setProducts(await productService.getAdminAll());
    } catch (error) {
      setProducts([]);
    }
  };

  const renderContent = () => {
    if (menusLoading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-muted border-t-primary"></div>
        </div>
      );
    }

    if (menusError) {
      return (
        <MenuStatusMessage
          title="Menu indisponível"
          message="Não foi possível carregar as telas liberadas para o seu usuário."
          onRetry={() => fetchMenus()}
        />
      );
    }

    if (!activeTab || !canAccess(activeTab)) {
      return (
        <MenuStatusMessage
          title="Sem acesso"
          message="Seu perfil não tem telas liberadas. Fale com o administrador do sistema."
        />
      );
    }

    switch (activeTab) {
      case "dashboard":
        return (
          <Dashboard
            menuItems={menuItems}
            canAccess={canAccess}
            onNavigate={handleTabChange}
            pendingCount={pendingCount}
          />
        );
      case "products":
        return <ProductsManagement />;
      case "categories":
        return <CategoriesManagement />;
      case "coupons":
        return <CouponsManagement />;
      case "ingredients":
        return <IngredientsManagement />;
      case "uploads":
        return <UploadsManagement />;
      case "users":
        return <UsersManagement />;
      case "clients":
        return <ClientsManagement />;
      case "addresses":
        return <AddressesManagement />;
      case "storage-locations":
        return <StorageLocationsManagement />;
      case "equipments":
        return <EquipmentsManagement />;
      case "stocks":
        return (
          <StocksManagement
            onOpenOrder={canAccess("orders") ? openOrder : undefined}
          />
        );
      case "orders":
        return (
          <OrdersManagement
            initialStatus={orderFilterStatus}
            pendingCount={pendingCount}
            focusOrderCode={orderFocusCode}
            onFocusHandled={clearOrderFocus}
          />
        );
      case "payments":
        return <PaymentMethodsManagement />;
      case "payment-integrations":
        return <PaymentIntegrationsManagement />;
      case "pix":
        return <PixManagement />;
      case "companies":
        return <CompaniesManagement />;
      case "freight":
        return <FreightManagement />;
      case "etiquetas":
        return <EtiqueLabelGenerator products={products} />;
      default:
        return (
          <MenuStatusMessage
            title="Tela não encontrada"
            message={`A tela "${activeTab}" está no menu, mas ainda não existe no painel.`}
          />
        );
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header
        setActiveTab={handleTabChange}
        setSidebarOpen={setSidebarOpen}
        setOrderFilterStatus={setOrderFilterStatus}
        pendingCount={pendingCount}
      />
      <div className="flex flex-1 flex-col lg:flex-row">
        <Sidebar
          menuItems={menuItems}
          menusLoading={menusLoading}
          menusError={menusError}
          onRetry={() => fetchMenus()}
          activeTab={activeTab}
          onSelect={handleTabChange}
          open={sidebarOpen}
          onClose={closeSidebar}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="w-full flex-1 overflow-x-hidden px-3.5 pb-28 pt-4 md:px-6 md:pt-6 lg:px-8 lg:pb-8 lg:pt-8">
            {isLoadingPage ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-muted border-t-primary mb-4"></div>
                  <p className="text-muted-foreground font-medium">Carregando...</p>
                </div>
              </div>
            ) : (
              <div key={companyScopeKey}>{renderContent()}</div>
            )}
          </main>

          {/* Rodapé (no celular fica acima da barra inferior) */}
          <footer className="hidden border-t bg-card/60 px-6 py-4 lg:block">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <p>
                © {new Date().getFullYear()} Cardápio Bonfim. Todos os direitos
                reservados.
              </p>
              <p className="font-semibold text-primary">
                v{process.env.REACT_APP_VERSION || "1.0.0"}
              </p>
            </div>
          </footer>
        </div>
      </div>

      {!menusLoading && !menusError && (
        <BottomNav
          canAccess={canAccess}
          activeTab={activeTab}
          onSelect={handleTabChange}
          onOpenMenu={() => setSidebarOpen(true)}
          pendingCount={pendingCount}
        />
      )}
    </div>
  );
};

// (moved into separate component file: src/components/categories/CategoriesManagement.jsx)

// Categories Management Component

// Categories Management Component
const UploadsManagement = () => {
  return <UploadsManagementModal />;
};

// Main App Component
function App() {
  const { isOpen, title, message, type, confirmText, onConfirm, closeError } =
    useErrorStore();

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/print-order" element={<PrintOrderReceipt />} />
            <Route path="/*" element={<ProtectedRoute />} />
          </Routes>

          {/* Modal de Erro Global */}
          <ErrorModal
            open={isOpen}
            onClose={closeError}
            title={title}
            message={message}
            type={type}
            confirmText={confirmText}
            onConfirm={onConfirm}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Protected Route Component
const ProtectedRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <AdminPanel />;
};

// Login Route Component
const LoginRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" />;
  }

  return <Login />;
};

export default App;
